import React, { useEffect } from 'react';
import { ShieldCheck, Lock, AlertTriangle, CheckCircle, X } from 'lucide-react';

interface ChatDisclaimerModalProps {
  isOpen: boolean;
  onAgree: () => void;
  /**
   * 전달되면 '재열람 모드' — 프로필 메뉴에서 안내를 다시 열어본 경우다.
   * 오버레이·ESC·X로 닫을 수 있고, 동의 기록(recordDisclaimerAgreement)을 갱신하지 않는다.
   * 미전달 시에는 기존대로 동의 버튼 외에는 빠져나갈 수 없다.
   */
  onClose?: () => void;
}

export const ChatDisclaimerModal: React.FC<ChatDisclaimerModalProps> = ({
  isOpen,
  onAgree,
  onClose,
}) => {
  const isReview = !!onClose;

  // 모달 열릴 때 스크롤 방지
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // ESC로 닫기 — 재열람 모드에서만 (최초 동의는 강제)
  useEffect(() => {
    if (!isOpen || !onClose) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* 오버레이 (동의 모드에서는 클릭으로 닫기 불가) */}
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />

      {/* 모달 컨텐츠 */}
      <div className="relative w-full max-w-lg mx-4 bg-white rounded-2xl shadow-xl animate-modal-enter">
        {/* 헤더 */}
        <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-100">
          <ShieldCheck size={24} className="text-emerald-600" />
          <h2 className="flex-1 text-lg font-semibold text-slate-800">채팅 이용 안내</h2>
          {onClose && (
            <button
              onClick={onClose}
              aria-label="닫기"
              className="p-2 -mr-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <X size={20} aria-hidden="true" />
            </button>
          )}
        </div>

        {/* 본문 - 정책 카드 */}
        <div className="px-6 py-4 space-y-3 max-h-[60vh] overflow-y-auto">
          {/* 메시지 보안 */}
          <div className="flex gap-3 p-4 bg-blue-50 border border-blue-100 rounded-lg">
            <Lock size={20} className="text-blue-600 shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-semibold text-blue-700 mb-1">메시지 보안</h3>
              <p className="text-xs text-slate-600 leading-relaxed">
                모든 메시지는 암호화되어 전송되며, 서버에 저장됩니다.
                업무 목적에 맞게 안전하게 이용해 주세요.
              </p>
            </div>
          </div>

          {/* 금지 사항 */}
          <div className="flex gap-3 p-4 bg-amber-50 border border-amber-100 rounded-lg">
            <AlertTriangle size={20} className="text-amber-600 shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-semibold text-amber-700 mb-1">금지 사항</h3>
              <p className="text-xs text-slate-600 leading-relaxed">
                개인정보·기밀정보 공유, 타인 명예훼손, 비속어 사용 등은 엄격히 금지됩니다.
                위반 시 사내 규정에 따라 조치될 수 있습니다.
              </p>
            </div>
          </div>

          {/* 대화 내용의 보호와 열람 — "감시"가 아니라 "통제된 열람"으로 읽히게 쓴다.
              눈 아이콘은 지켜본다는 인상을 주므로 방패 아이콘을 쓴다 */}
          <div className="flex gap-3 p-4 bg-emerald-50 border border-emerald-100 rounded-lg">
            <ShieldCheck size={20} className="text-emerald-600 shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-semibold text-emerald-700 mb-1">대화 내용의 보호와 열람</h3>
              <p className="text-xs text-slate-600 leading-relaxed">
                대화 내용은 암호화되어 보관되며, 법령과 사내 규정에 따라 필요한 경우에만 지정된 담당자가
                사유를 남기고 열람할 수 있습니다. 모든 열람은 기록되고 변조 여부가 검증됩니다.
              </p>
            </div>
          </div>
        </div>

        {/* 푸터 */}
        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 rounded-b-2xl">
          <p className="text-xs text-slate-500 mb-3 text-center">
            {isReview
              ? '이 안내는 프로필 메뉴에서 언제든 다시 볼 수 있습니다.'
              : '위 안내 사항을 확인하였으며, 이에 동의합니다.'}
          </p>
          <button
            onClick={isReview ? onClose : onAgree}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <CheckCircle size={18} />
            {isReview ? '확인' : '동의 후 시작'}
          </button>
        </div>
      </div>
    </div>
  );
};
